import { addDisplayName, addHandlers, addProps, addRef, flowMax } from 'ad-hok'
import { ColDef, FilterChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { CreatorEarnings } from 'shared/src/api/types'
import { addNavigate, addPathParam } from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { Button } from 'components/Button'
import { Page } from 'components/Page'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import {
  COLUMN_TYPES,
  DEFAULT_COL_DEF,
  DOLLAR_AMOUNT_COLUMN_WIDTH,
  dateComparator,
  displayedEarnings,
  formatNumber,
  formatPrice,
  getAggregateRow,
  onFilterChanged,
  totalEarnings,
} from './utils'

const useStyles = createUseStyles({
  table: {
    marginTop: 20,
    width: '100%',
    height: '90dvh',
  },
})

export const EarningsUser: FC = flowMax(
  addDisplayName('EarningsUser'),
  addPathParam('userId'),
  addApi,
  addDataLoadingFromPromise(
    'creatorEarnings',
    ({ userId, api }) => api.creatorEarnings.getForUser(userId),
    []
  ),
  addCmsLoadingAndErrorIndicators('creatorEarnings'),
  addProps(({ creatorEarnings }) => ({
    username: creatorEarnings.length > 0 ? creatorEarnings[0].username : '',
  })),
  addTranslationHelpers,
  addProps(({ t }) => ({
    columns: typedAs<ColDef<CreatorEarnings>[]>([
      {
        headerName: t('admin.earnings.columns.date'),
        field: 'date',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
      },
      {
        headerName: t('admin.earnings.columns.mediaViews'),
        field: 'mediaViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaViewEarnings'),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaViews'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.productViews'),
        field: 'productViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.productViewEarnings'),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'productViews'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClicks'),
        field: 'outgoingProductClicks',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClickEarnings'),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'outgoingProductClicks'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublished'),
        field: 'mediaPublished',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublishedEarnings'),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaPublished'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaTagged'),
        field: 'mediaTagged',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaTaggedEarnings'),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaTagged'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.editsCreated'),
        field: 'editsCreated',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.editsCreatedEarnings'),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'editsCreated'),
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.bonusEarnings'),
        type: ['number'],
        field: 'bonusEarnings',
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.partnershipEarnings'),
        type: ['number'],
        field: 'partnershipEarnings',
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.totalEarnings'),
        type: ['number'],
        valueGetter: ({ data }) => data && totalEarnings(data),
        valueFormatter: formatPrice,
      },
    ]),
  })),
  addRef('gridRef', typedAs<AgGridReact | null>(null)),
  addNavigate,
  addHandlers({
    onGridReady:
      ({ gridRef, creatorEarnings }) =>
      () => {
        gridRef.current?.api?.setPinnedTopRowData(
          getAggregateRow(creatorEarnings)
        )
      },
    onExport:
      ({ username, gridRef, t }) =>
      () => {
        gridRef.current!.api.exportDataAsCsv({
          fileName: t('admin.earnings.export.user', { username }),
        })
      },
    onFilterChanged:
      ({ creatorEarnings }) =>
      (event: FilterChangedEvent) => {
        onFilterChanged(event, creatorEarnings)
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    username,
    creatorEarnings,
    onGridReady,
    onExport,
    onFilterChanged,
    columns,
    gridRef,
    classes,
    t,
  }) => (
    <Page leftButton="">
      <h1>{username}</h1>
      <Button onClick={onExport} text={t('admin.earnings.export.button')} />
      <div className={cx('ag-theme-alpine', classes.table)}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columns}
          columnTypes={COLUMN_TYPES}
          defaultColDef={DEFAULT_COL_DEF}
          rowData={creatorEarnings}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
        />
      </div>
    </Page>
  )
)
